import { useParams } from 'react-router-dom';

import { useToast } from '../../../app/provider/useToast';
import { appConfig } from '../../../config/app-config';
import Button from '../../../shared/components/Button';
import Loader from '../../../shared/components/Loader';
import { SHARE_ERROR_MESSAGES } from '../constants/messages';
import { useAddSharedBasketToCart } from '../hooks/useAddSharedBasketToCart';
import { useViewSharedBasket } from '../hooks/useViewSharedBasket';
import { LineItemSnapshot } from '../types/sharedBasket.types';

const formatPrice = (centAmount: number, currencyCode: string) =>
  new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currencyCode || 'USD',
    maximumFractionDigits: 0,
  }).format(centAmount / 100);

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const SharedBasketPage = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const { showError, showSuccess } = useToast();

  const { loading, error, sharedBasket } = useViewSharedBasket({ shareToken, showError });
  const { addingToCart, handleAddToCart } = useAddSharedBasketToCart({ sharedBasket, showError, showSuccess });

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 160px)' }}>
        <Loader text="Loading shared basket..." fullScreen={true} />
      </div>
    );
  }

  if (error) {
    const errorMessages: Record<string, string> = {
      expired: SHARE_ERROR_MESSAGES.EXPIRED_LINK,
      'not-found': SHARE_ERROR_MESSAGES.LINK_NOT_FOUND,
      'no-products': 'Products are no longer available',
    };
    const errorMessage = errorMessages[error] ?? SHARE_ERROR_MESSAGES.ADD_TO_CART_FAILED;

    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto text-center">
          <div className="text-6xl mb-4">?</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{errorMessage}</h1>
          <p className="text-gray-600 mb-8">Redirecting you to the home page...</p>
        </div>
      </div>
    );
  }

  if (!sharedBasket) return null;

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> Prices shown are current and may differ from when this basket was shared.
          </p>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Shared Basket</h1>
        <p className="text-gray-600">
          Review the items below and add them to your basket to proceed with checkout.
        </p>
      </div>

      {/* Line Items */}
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200 mb-6">
        {sharedBasket.lineItems.map((item: LineItemSnapshot, index: number) => {
          const product = sharedBasket.productMap[item.productId];
          if (!product) return null;

          const masterData = product.masterData.current;
          const variant = masterData.masterVariant;
          const productName =
            masterData.name[appConfig.defaults.locale] ??
            masterData.name['en'] ??
            Object.values(masterData.name)[0];

          const delegateRate = item.customFields?.delegateRate;
          const customerGroup = delegateRate ? sharedBasket.customerGroupMap[delegateRate] : null;

          const priceObj =
            (customerGroup
              ? variant.prices?.find((p) => p.customerGroup?.id === customerGroup.id)
              : undefined) ?? variant.prices?.[0];

          const currentPrice =
            priceObj?.discounted?.value?.centAmount ?? priceObj?.value?.centAmount ?? 0;
          const currencyCode = priceObj?.value?.currencyCode ?? sharedBasket.currency ?? 'USD';
          const hasDiscount = !!priceObj?.discounted;
          const originalPrice = hasDiscount ? priceObj!.value.centAmount : null;

          const attributes = variant.attributes ?? [];
          const startDate = attributes.find((a) => a.name === 'startDate')?.value as string | undefined;
          const endDate = attributes.find((a) => a.name === 'endDate')?.value as string | undefined;

          return (
            <div key={`${item.productId}-${index}`} className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{productName}</h3>

                  {(startDate || endDate) && (
                    <p className="text-sm text-gray-600 mb-2">
                      {formatDate(startDate)} - {formatDate(endDate)}
                    </p>
                  )}

                  <div className="text-sm text-gray-500">Quantity: {item.quantity}</div>

                  {delegateRate && (
                    <div className="text-xs text-gray-500 mt-1">
                      Attendee Type: {delegateRate}
                    </div>
                  )}

                  {item.customFields?.selectedOptionsList && (
                    <div className="text-xs text-gray-500">
                      Selected Options:{' '}
                      {Array.isArray(item.customFields.selectedOptionsList)
                        ? item.customFields.selectedOptionsList.join(', ')
                        : item.customFields.selectedOptionsList}
                    </div>
                  )}
                </div>

                <div className="text-right ml-4">
                  <div className="text-lg font-semibold text-gray-900">
                    {formatPrice(currentPrice * item.quantity, currencyCode)}
                  </div>
                  {hasDiscount && originalPrice !== null && (
                    <div className="text-sm text-gray-500 line-through">
                      {formatPrice(originalPrice * item.quantity, currencyCode)}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    {formatPrice(currentPrice, currencyCode)} each
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <Button
          onClick={() => window.history.back()}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
        >
          Back to Home
        </Button>

        <Button
          onClick={handleAddToCart}
          disabled={addingToCart}
          className="px-8 py-3 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {addingToCart ? 'Adding to Basket...' : 'Add to Basket'}
        </Button>
      </div>
    </div>
  );
};

export default SharedBasketPage;
